import { Button } from "@openanalyst-ai/ui/button"
import { useDialog } from "@openanalyst-ai/ui/context/dialog"
import { Dialog } from "@openanalyst-ai/ui/dialog"
import { TextField } from "@openanalyst-ai/ui/text-field"
import { isPlainProjectFolderName } from "@openanalyst-ai/core/util/project-folder-name"
import { createSignal } from "solid-js"
import { useLanguage } from "@/context/language"

/**
 * The step in front of the old "Custom project" action.
 *
 * Before this, clicking Custom project fired the native folder picker straight away, so the
 * only way to CREATE a project folder was to take one of the starter templates and its whole
 * file tree with it. Browse stays exactly as it was — it is one of the three actions here.
 *
 * Layout is one name field over a right-aligned action row, with Back returning to the
 * starter grid the user came from.
 */
export function DialogNewProject(props: {
  /** Where "Create Folder" will put it — surfaced under the field so the destination is never a surprise. */
  rootLabel: string
  onBack: () => void
  onCreateFolder: (name: string) => Promise<void> | void
  onBrowseFolder: () => Promise<void> | void
  onCustomTemplate: (name: string) => Promise<void> | void
}) {
  const dialog = useDialog()
  const language = useLanguage()
  const [name, setName] = createSignal("")

  // Blank is fine — the main process falls back to "OpenAnalyst Project" and its n+1
  // suffixing. Only a name that is not a plain folder name blocks the create actions.
  const invalid = () => name().trim().length > 0 && !isPlainProjectFolderName(name())

  const run = (action: (name: string) => Promise<void> | void) => {
    // Read the name BEFORE closing — the handoff must not depend on this component's
    // signals still being readable after the dialog is disposed.
    const typed = name().trim()
    dialog.close()
    // The dialog owns no work of its own: it closes first, then hands off, exactly like
    // DialogProjectStart does, so a slow create never leaves a dead dialog on screen.
    queueMicrotask(() => void action(typed))
  }

  const back = (
    <Button
      variant="ghost"
      size="small"
      icon="arrow-left"
      class="-ml-2"
      onClick={() => {
        dialog.close()
        queueMicrotask(() => props.onBack())
      }}
    >
      {language.t("project.start.back")}
    </Button>
  )

  return (
    // `fit` matters here: without it the dialog container is a FIXED height (512px, or
    // 600px at size="large") with the content pinned to its top, which leaves dead space
    // under the buttons and pushes the box above the centre of the screen.
    <Dialog title={back} fit transition>
      <div data-dialog-new-project="" class="flex flex-col gap-6 px-6 pb-6 pt-2">
        <TextField
          autofocus
          type="text"
          label={language.t("project.start.new.name")}
          hideLabel
          placeholder={language.t("project.start.new.placeholder")}
          value={name()}
          error={invalid() ? language.t("project.start.new.nameInvalid") : undefined}
          validationState={invalid() ? "invalid" : undefined}
          description={language.t("project.start.new.create.description", { path: props.rootLabel })}
          onChange={setName}
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key !== "Enter" || invalid()) return
            event.preventDefault()
            run((typed) => props.onCreateFolder(typed))
          }}
        />

        <div class="flex flex-wrap items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => run(() => props.onBrowseFolder())}>
            {language.t("project.start.new.browse.title")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={invalid()}
            onClick={() => run((typed) => props.onCustomTemplate(typed))}
          >
            {language.t("project.start.new.template.title")}
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={invalid()}
            onClick={() => run((typed) => props.onCreateFolder(typed))}
          >
            {language.t("project.start.new.create.title")}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
